import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Back, Inline, Primary, Secondary, Tag, day, inputCls, money, when } from "@/components/backoffice/kit";
import { Badges, NextAction } from "@/components/backoffice/OrderCard";
import { PayLinkSheet, ProofSheet, ReadySheet, ReorderSheet, ReprintSheet } from "@/components/backoffice/sheets";
import FulfillmentBox from "@/components/backoffice/FulfillmentBox";

const PROOF_TEXT = { waiting: "Waiting for the customer", approved: "Approved", changes: "Changes asked" };

function Line({ item, printed, order, options, onSaved, planEndpoint }) {
    const img = item.proof?.url || item.artwork?.find((a) => a.url)?.url;
    const sel = item.selections || {};
    const size = sel.size_summary || sel.size || (sel.sizes ? Object.entries(sel.sizes).filter(([, n]) => n).map(([k, n]) => `${k.split("|").pop()} ×${n}`).join(", ") : "") || (sel.width && sel.height ? `${sel.width} × ${sel.height} in` : "");
    return (
        <li className="border border-[#1a1a1a] bg-white p-3" data-testid={`line-${item.uid}`}>
            <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-3">
                <div className="h-[88px] w-[88px] border border-[#767676]">
                    {img ? <img src={img} alt="" className="h-full w-full object-contain" /> : <span className="flex h-full items-center justify-center text-[11px] text-[#767676]">no image</span>}
                </div>
                <div className="min-w-0 text-[15px]">
                    <p className="text-[16px] font-semibold">{item.product_name}</p>
                    <p>{size ? `${size} · ` : ""}Qty {item.quantity} · {money(item.line_total)}</p>
                    {item.summary && <p className="text-[#4d4d4d]">{item.summary}</p>}
                    <div className="mt-1 flex flex-wrap gap-1.5">
                        <Tag tone={item.fulfillment === "consignment" ? "ink" : "grey"}>{item.fulfillment === "consignment" ? "13thav prints" : "You print"}</Tag>
                        {item.print_ready === false && <Tag tone={item.cleaned_at ? "green" : "red"}>{item.cleaned_at ? "Cleaned" : "Needs cleanup"}</Tag>}
                        {item.cleanup_fee ? <Tag tone="grey">Cleanup paid</Tag> : null}
                        {item.customer_supplied && <Tag tone="grey">Customer-supplied</Tag>}
                    </div>
                    {(item.artwork || []).map((a, n) => a.url && (
                        <a key={n} href={a.url} target="_blank" rel="noreferrer" className="mt-1 block truncate text-[14px] underline" data-testid={`line-file-${item.uid}-${n}`}>{a.filename || "file"}</a>
                    ))}
                </div>
            </div>
            {item.note && <p className="mt-3 border-l-4 border-[var(--accent)] pl-3 text-[20px] leading-snug" data-testid={`line-note-${item.uid}`}>{item.note}</p>}
            {printed && <div className="mt-3"><FulfillmentBox orderId={order.id} item={item} options={options} endpoint={planEndpoint?.(item)} onSaved={onSaved} /></div>}
        </li>
    );
}

/**
 * 8.3 Order detail — one screen, Back to the gallery (filters kept), primary = the next status button.
 * `platform` renders it for the admin production queue (8.11): same screen plus the shop name and Text shop.
 */
export default function OrderDetail({ orderId, back, platform = false, shopPhone }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");
    const [sheet, setSheet] = useState(null);
    const [busy, setBusy] = useState(false);
    const [undo, setUndo] = useState(null);
    const [needBy, setNeedBy] = useState("");
    const base = platform ? "/admin/orders" : "/vendor/orders";

    const load = useCallback(() => {
        setErr("");
        api.get(`${base}/${orderId}/detail`).then(({ data }) => { setD(data); setNeedBy(data.order.need_by || ""); })
            .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
    }, [base, orderId]);
    useEffect(() => { load(); }, [load]);

    const setOrder = (order) => setD((prev) => ({ ...prev, order }));
    const advance = async (order, status) => {
        if (status === "ready") return setSheet("ready");
        setBusy(true);
        try { const { data } = await api.post(`${base}/${order.id}/advance`, { status }); setOrder(data.order); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    const cancel = async () => {
        try {
            const { data } = await api.post(`${base}/${orderId}/cancel`);
            setOrder(data.order);
            const t = setTimeout(() => setUndo(null), 5000);
            setUndo({ t });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const undoCancel = async () => {
        clearTimeout(undo?.t); setUndo(null);
        try { const { data } = await api.post(`${base}/${orderId}/uncancel`); setOrder(data.order); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const saveNeedBy = async () => {
        try { const { data } = await api.patch(`${base}/${orderId}/meta`, { need_by: needBy }); setOrder(data.order); toast.success("Need-by saved"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (err) return <div className="grid gap-3"><Back to={back} /><Inline error={err} onRetry={load} testid="detail-error" /></div>;
    if (!d) return <div className="flex min-h-[40vh] items-center justify-center" data-testid="detail-loading"><Loader2 className="animate-spin" /></div>;
    const o = d.order;
    const contact = o.contact || {};
    const phone = (contact.phone || "").replace(/[^\d+]/g, "");
    const smsBody = encodeURIComponent(`Hi ${o.first_name || ""}, about order ${o.order_number}: `);
    const printed = (item) => platform ? item.made_by_13thav !== false : (o.prints !== "consignment" && item.fulfillment !== "consignment");
    const shopSms = (shopPhone || o.shop_phone || "").replace(/[^\d+]/g, "");
    const planEndpoint = platform ? (item) => `/admin/orders/${o.id}/items/${item.uid}/fulfillment` : null;

    return (
        <div className="grid gap-4" data-testid="order-detail">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <Back to={back} testid="detail-back" />
                {(!platform || o.prints === "self") && <Link to={`/account/orders/${o.id}/ticket`} className="min-h-[44px] inline-flex items-center underline" data-testid="detail-ticket">Print job ticket</Link>}
            </div>
            <header className="grid gap-2">
                <div className="flex flex-wrap items-baseline gap-x-3">
                    <h1 className="text-[24px] font-semibold" data-testid="detail-code">{o.order_number}</h1>
                    <span className="text-[18px] font-semibold tabular-nums" data-testid="detail-total">{money(platform ? o.order_total : o.shop_total)}</span>
                    {!platform && o.prints !== "self" && <span className="text-[15px] text-[#4d4d4d]" data-testid="detail-keep">You keep {money(o.my_total)}</span>}
                    {platform && o.shop_name && <span className="text-[16px] text-[#4d4d4d]" data-testid="detail-shop">{o.shop_name}</span>}
                    {o.is_test && <Tag tone="red">Test</Tag>}
                </div>
                <Badges order={o} testid="detail-badges" />
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[15px]">
                    {o.ready_by && <span data-testid="detail-ready">Ready by <b>{day(o.ready_by)}</b></span>}
                    <label className="inline-flex items-center gap-2">Need by
                        <input type="date" className={`${inputCls} !w-auto`} value={needBy} onChange={(e) => setNeedBy(e.target.value)} onBlur={() => needBy !== (o.need_by || "") && saveNeedBy()} data-testid="detail-needby" />
                        {o.need_by_late && <Tag tone="red">before ready-by</Tag>}
                    </label>
                    <span data-testid="detail-handoff">{o.handoff === "ship" ? "Ship" : "Pickup"}</span>
                    <span data-testid="detail-paid">{o.paid ? `Paid${o.paid_by === "card" ? " by card" : ""}` : "Not paid"}</span>
                </div>
                <p className="text-[16px]" data-testid="detail-customer">
                    <b>{contact.name || "Customer"}</b>{contact.phone ? ` · ${contact.phone}` : ""}{contact.email ? ` · ${contact.email}` : ""}
                </p>
            </header>

            <div data-testid="detail-primary"><NextAction order={o} onAdvance={advance} busy={busy} full size="detail" /></div>

            <div className="flex flex-wrap gap-2" data-testid="detail-actions">
                {phone && <Secondary href={`sms:${phone}?&body=${smsBody}`} testid="detail-text">Text</Secondary>}
                {contact.email && <Secondary href={`mailto:${contact.email}?subject=${encodeURIComponent(`Order ${o.order_number}`)}`} testid="detail-email">Email</Secondary>}
                {platform && shopSms && <Secondary href={`sms:${shopSms}?&body=${encodeURIComponent(`About order ${o.order_number}: `)}`} testid="detail-text-shop">Text shop</Secondary>}
                <Secondary onClick={() => setSheet("proof")} testid="detail-send-proof">Send proof</Secondary>
                <Secondary onClick={() => setSheet("paylink")} testid="detail-pay-link">Send pay link</Secondary>
                <Secondary onClick={() => setSheet("reprint")} testid="detail-reprint">{o.prints === "self" || platform ? "Reprint" : "Ask 13thav to reprint"}</Secondary>
                <Secondary onClick={() => setSheet("reorder")} testid="detail-reorder">Reorder for customer</Secondary>
                {o.can_cancel && o.status === "pending_payment" && <Secondary onClick={cancel} danger testid="detail-cancel">Cancel order</Secondary>}
            </div>
            {undo && (
                <div className="flex items-center justify-between border border-[#1a1a1a] bg-white p-3" data-testid="detail-undo">
                    <span className="text-[15px]">Order cancelled.</span>
                    <Primary onClick={undoCancel} testid="detail-undo-btn">Undo</Primary>
                </div>
            )}

            <ul className="grid gap-3" data-testid="detail-lines">
                {(o.items || []).concat(o.consigned_items || []).map((it) => (
                    <Line key={it.uid} item={it} printed={printed(it)} order={o} options={d.plan_options} onSaved={setOrder} planEndpoint={planEndpoint} />
                ))}
            </ul>

            <section className="grid gap-1 text-[15px]" data-testid="detail-proof">
                <h2 className="text-[16px] font-semibold">Proof</h2>
                <p>{o.proof_badge ? PROOF_TEXT[o.proof_badge] : "No proof sent yet"}{o.proof_sent_at ? ` · sent ${when(o.proof_sent_at)}` : ""}</p>
                {o.proof_badge === "changes" && o.proof_note && <p className="border-l-4 border-[#b3261e] pl-3 text-[18px]" data-testid="detail-proof-note">“{o.proof_note}”</p>}
                {o.proof_badge === "changes" && <div><Primary onClick={() => setSheet("proof")} testid="detail-new-proof">Send new proof</Primary></div>}
            </section>

            <section className="text-[15px]" data-testid="detail-timeline">
                <h2 className="text-[16px] font-semibold">Timeline</h2>
                <ul className="divide-y divide-[#767676] border-y border-[#767676]">
                    <li className="py-1.5">Placed · {when(o.created_at)}</li>
                    {o.paid_at && <li className="py-1.5">Paid · {when(o.paid_at)}</li>}
                    {(o.timeline || []).map((t, n) => <li key={n} className="py-1.5 capitalize">{(t.status || "").replace("_", " ")} · {when(t.at)}</li>)}
                    {(o.payments || []).map((p, n) => <li key={`p${n}`} className="py-1.5">{money(p.amount)} paid{p.for ? ` for ${p.for}` : ""} · {when(p.paid_at)}</li>)}
                    {o.reprint_of && <li className="py-1.5">Reprint of {o.reprint_of}{o.reprint_reason ? ` — ${o.reprint_reason}` : ""}</li>}
                    {o.reordered_from && <li className="py-1.5">Reordered from {o.reordered_from}</li>}
                </ul>
            </section>

            {sheet === "ready" && <ReadySheet order={o} base={base} pickupMode={d.pickup_mode} carriers={d.carriers} onDone={(ord) => { setOrder(ord); setSheet(null); toast.success(ord.handoff === "ship" ? "Shipped — tracking sent" : "Ready — code sent to the customer"); }} onClose={() => setSheet(null)} />}
            {sheet === "proof" && <ProofSheet order={o} base={base} onDone={(ord) => { setOrder(ord); setSheet(null); }} onClose={() => setSheet(null)} />}
            {sheet === "paylink" && <PayLinkSheet order={o} base={base} rushPct={d.rush_pct} artworkDefault={d.artwork_default} onDone={load} onClose={() => setSheet(null)} />}
            {sheet === "reprint" && <ReprintSheet order={o} base={base} onDone={() => { setSheet(null); load(); }} onClose={() => setSheet(null)} />}
            {sheet === "reorder" && <ReorderSheet order={o} base={base} onClose={() => setSheet(null)} />}
        </div>
    );
}
